import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Lock } from 'lucide-react';
import { useSubmission } from '../../hooks/useSubmission';
import EditableMediaList from '../../components/submission/EditableMediaList';
import TipTapEditor from '../../components/submission/TipTapEditor';
import Hero from '../../components/ui/Hero';
import { useAuth } from '../../hooks/useAuth';

const SubmitNewPage = () => {
  const navigate = useNavigate();
  const { submitAnimatronic, isLoading, error } = useSubmission();
  const { user, signIn } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    status: 'active',
    type: '',
    country: '',
    park: '',
    attraction: '',
    gps_coordinates: '',
    brand: '',
    media: [{
      id: `entry-${Math.random().toString(36).substr(2, 9)}`,
      url: '',
      type: 'image',
      credit: '',
      credit_url: ''
    }]
  });

  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp";

  if (!user) {
    return (
      <div>
        <Hero
          title="Submit New Animatronic"
          description="Help grow our database by submitting new animatronics"
          backgroundImage={heroImage}
          compact
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Lock className="w-12 h-12 text-brand mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Sign In Required</h2>
            <p className="text-content-secondary mb-6">
              You need to be signed in to submit new animatronics to the database.
            </p>
            <button
              onClick={signIn}
              className="btn-primary"
            >
              Sign In to Contribute
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await submitAnimatronic({
        ...formData,
        media: formData.media.map(({ id, ...rest }) => rest)
      }, 'new');
      
      navigate('/community/forum');
    } catch (err) {
      console.error('Submission error:', err);
    }
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <Hero
        title="Submit New Animatronic"
        description="Add a new animatronic to our database"
        compact
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                {error.message}
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Description *
                  </label>
                  <TipTapEditor
                    value={formData.description}
                    onChange={(value) => handleChange('description', value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Year *
                    </label>
                    <input
                      type="number"
                      value={formData.year}
                      onChange={(e) => handleChange('year', parseInt(e.target.value))}
                      required
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Status *
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleChange('status', e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                    >
                      <option value="active">Active</option>
                      <option value="retired">Retired</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type *
                  </label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    required
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Location Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    required
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Park/Venue *
                  </label>
                  <input
                    type="text"
                    value={formData.park}
                    onChange={(e) => handleChange('park', e.target.value)}
                    required
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Attraction *
                  </label>
                  <input
                    type="text"
                    value={formData.attraction}
                    onChange={(e) => handleChange('attraction', e.target.value)}
                    required
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    GPS Coordinates *
                  </label>
                  <input
                    type="text"
                    value={formData.gps_coordinates}
                    onChange={(e) => handleChange('gps_coordinates', e.target.value)}
                    required
                    placeholder="e.g., 28.3852, -81.5639"
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                  <p className="mt-1 text-sm text-content-secondary">
                    Format: latitude, longitude (e.g., 28.3852, -81.5639)
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Brand *
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-4">
                    Media Files
                  </label>
                  <EditableMediaList
                    entries={formData.media}
                    onChange={(media) => handleChange('media', media)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit for Review
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitNewPage;